import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { DIGITAL_FILTER_CUTOFF } from '@/tools/digitalFilter/digitalFilter.constants';
import type { BaseToolPersistedState, ToolItem } from '@/toolSelection/baseTool.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

type ValueWithUnits = ValueWithUnitsItem & { valid?: boolean };
type InitialValueWithUnits = Partial<ValueWithUnits>;

interface DigitalFilterState extends BaseToolPersistedState {
  inputSignal: ToolItem | undefined;
  filterType: string | undefined;
  color: string;
  cutoff: InitialValueWithUnits;
  cutoff2: InitialValueWithUnits;
  samplingRate: InitialValueWithUnits;
  windowSize: InitialValueWithUnits;
  isAutoSamplingRate: boolean;
  isAutoWindowSize: boolean;
}

export type DigitalFilterDehydratedState = DigitalFilterState;

export class DigitalFilterStore extends BaseToolStore {
  static readonly storeName = 'sqDigitalFilterStore';
  type = TREND_TOOLS.DIGITAL_FILTER;
  parameterDefinitions = {
    inputSignal: { predicate: ['name', 'inputSignal'] },
  };

  initialize(): void {
    const initialState: DigitalFilterState = {
      ...BASE_TOOL_COMMON_PROPS,
      filterType: undefined,
      cutoff: DIGITAL_FILTER_CUTOFF,
      cutoff2: DIGITAL_FILTER_CUTOFF,
      samplingRate: DIGITAL_FILTER_CUTOFF,
      windowSize: DIGITAL_FILTER_CUTOFF,
      isAutoSamplingRate: true,
      isAutoWindowSize: true,
      color: '',
      inputSignal: undefined,
    };
    this.state = this.immutable(initialState);
  }

  get inputSignal(): ToolItem | undefined {
    return this.state.get('inputSignal');
  }

  get filterType(): string | undefined {
    return this.state.get('filterType');
  }

  get color(): string {
    return this.state.get('color');
  }

  get cutoff(): ValueWithUnits {
    return this.state.get('cutoff');
  }

  get cutoff2(): ValueWithUnits {
    return this.state.get('cutoff2');
  }

  get samplingRate(): ValueWithUnits {
    return this.state.get('samplingRate');
  }

  get windowSize(): ValueWithUnits {
    return this.state.get('windowSize');
  }

  get isAutoSamplingRate(): boolean {
    return this.state.get('isAutoSamplingRate');
  }

  get isAutoWindowSize(): boolean {
    return this.state.get('isAutoWindowSize');
  }

  dehydrate(): DigitalFilterDehydratedState {
    return {
      ...this.getBasePersistedState(),
      inputSignal: this.state.get('inputSignal') as ToolItem | undefined,
      filterType: this.state.get('filterType') as string | undefined,
      color: this.state.get('color') as string,
      cutoff: this.state.get('cutoff') as ValueWithUnits,
      cutoff2: this.state.get('cutoff2') as ValueWithUnits,
      samplingRate: this.state.get('samplingRate') as ValueWithUnits,
      windowSize: this.state.get('windowSize') as ValueWithUnits,
      isAutoSamplingRate: Boolean(this.state.get('isAutoSamplingRate')),
      isAutoWindowSize: Boolean(this.state.get('isAutoWindowSize')),
    };
  }

  rehydrate(dehydratedState: Partial<DigitalFilterDehydratedState>): void {
    this.applyBasePersistedState(dehydratedState);

    if ('inputSignal' in dehydratedState) {
      this.state.set('inputSignal', dehydratedState.inputSignal);
    }
    if ('filterType' in dehydratedState) {
      this.state.set('filterType', dehydratedState.filterType);
    }
    if ('color' in dehydratedState && dehydratedState.color !== undefined) {
      this.state.set('color', dehydratedState.color);
    }
    if ('cutoff' in dehydratedState && dehydratedState.cutoff !== undefined) {
      this.state.set('cutoff', dehydratedState.cutoff);
    }
    if ('cutoff2' in dehydratedState && dehydratedState.cutoff2 !== undefined) {
      this.state.set('cutoff2', dehydratedState.cutoff2);
    }
    if ('samplingRate' in dehydratedState && dehydratedState.samplingRate !== undefined) {
      this.state.set('samplingRate', dehydratedState.samplingRate);
    }
    if ('windowSize' in dehydratedState && dehydratedState.windowSize !== undefined) {
      this.state.set('windowSize', dehydratedState.windowSize);
    }
    if ('isAutoSamplingRate' in dehydratedState && dehydratedState.isAutoSamplingRate !== undefined) {
      this.state.set('isAutoSamplingRate', dehydratedState.isAutoSamplingRate);
    }
    if ('isAutoWindowSize' in dehydratedState && dehydratedState.isAutoWindowSize !== undefined) {
      this.state.set('isAutoWindowSize', dehydratedState.isAutoWindowSize);
    }
  }

  protected readonly handlers = {
    ...this.baseHandlers,

    DIGITAL_FILTER_SET_FILTER_TYPE: (payload: { filterType: string }) => {
      this.state.set('filterType', payload.filterType);
    },

    DIGITAL_FILTER_SET_COLOR: (payload: { color: string }) => {
      this.state.set('color', payload.color);
    },

    DIGITAL_FILTER_SET_CUTOFF: (payload: { cutoff: ValueWithUnits }) => {
      this.state.set('cutoff', payload.cutoff);
    },

    DIGITAL_FILTER_SET_CUTOFF_2: (payload: { cutoff2: ValueWithUnits }) => {
      this.state.set('cutoff2', payload.cutoff2);
    },

    DIGITAL_FILTER_SET_SAMPLING_RATE: (payload: { samplingRate: ValueWithUnits }) => {
      this.state.set('samplingRate', payload.samplingRate);
    },

    DIGITAL_FILTER_SET_WINDOW_SIZE: (payload: { windowSize: ValueWithUnits }) => {
      this.state.set('windowSize', payload.windowSize);
    },

    DIGITAL_FILTER_SET_IS_AUTO_SAMPLING_RATE: (payload: { isAutoSamplingRate: boolean }) => {
      this.state.set('isAutoSamplingRate', payload.isAutoSamplingRate);
    },

    DIGITAL_FILTER_SET_IS_AUTO_WINDOW_SIZE: (payload: { isAutoWindowSize: boolean }) => {
      this.state.set('isAutoWindowSize', payload.isAutoWindowSize);
    },
  };
}
